import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:8080';
const API_PREFIX = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const auth = {
  login: (username, password) =>
    api.post(`${API_PREFIX}/auth/login`, { username, password }),

  register: (username, email, password) =>
    api.post(`${API_PREFIX}/auth/register`, { username, email, password }),

  getCurrentUser: () => api.get(`${API_PREFIX}/auth/me`),

  resetPassword: (currentPassword, newPassword) =>
    api.post(`${API_PREFIX}/auth/reset-password`, { current_password: currentPassword, new_password: newPassword }),
};

export const users = {
  getAll: () => api.get(`${API_PREFIX}/users`),
  create: (data) => api.post(`${API_PREFIX}/users`, data),
  update: (id, data) => api.put(`${API_PREFIX}/users/${id}`, data),
  delete: (id) => api.delete(`${API_PREFIX}/users/${id}`),
};

export const repositories = {
  getAll: () => api.get(`${API_PREFIX}/repositories`),
  create: (data) => api.post(`${API_PREFIX}/repositories`, data),
  update: (id, data) => api.put(`${API_PREFIX}/repositories/${id}`, data),
  delete: (id) => api.delete(`${API_PREFIX}/repositories/${id}`),
};

export const jobs = {
  getAll: () => api.get(`${API_PREFIX}/jobs`),
  getOne: (id) => api.get(`${API_PREFIX}/jobs/${id}`),
  getLogs: (id) => api.get(`${API_PREFIX}/jobs/${id}/logs`),
  cancel: (id) => api.post(`${API_PREFIX}/jobs/${id}/cancel`),
  delete: (id) => api.delete(`${API_PREFIX}/jobs/${id}`),
};

export const stats = {
  get: () => api.get(`${API_PREFIX}/stats`),
};

export const projects = {
  getAll: () => api.get(`${API_PREFIX}/projects`),
  getOne: (id) => api.get(`${API_PREFIX}/projects/${id}`),
  create: (data) => api.post(`${API_PREFIX}/projects`, data),
  update: (id, data) => api.put(`${API_PREFIX}/projects/${id}`, data),
  delete: (id) => api.delete(`${API_PREFIX}/projects/${id}`),
  deploy: (id) => api.post(`${API_PREFIX}/projects/${id}/deploy`),
};

export const tickets = {
  getAllForProject: (projectId) => api.get(`${API_PREFIX}/projects/${projectId}/tickets`),
  getOne: (id) => api.get(`${API_PREFIX}/tickets/${id}`),
  create: (projectId, data) => api.post(`${API_PREFIX}/projects/${projectId}/tickets`, data),
  update: (id, data) => api.put(`${API_PREFIX}/tickets/${id}`, data),
  updateStatus: (id, status) => api.put(`${API_PREFIX}/tickets/${id}/status`, { status }),
  delete: (id) => api.delete(`${API_PREFIX}/tickets/${id}`),
  submitJob: (id) => api.post(`${API_PREFIX}/tickets/${id}/submit-job`),
  improveDescription: (description) => api.post(`${API_PREFIX}/tickets/improve-description`, { description }),
  submitPlanJob: (id) => api.post(`${API_PREFIX}/tickets/${id}/submit-plan-job`),
  submitReviewJob: (id) => api.post(`${API_PREFIX}/tickets/${id}/submit-review-job`),
  getPlan: (id) => api.get(`${API_PREFIX}/tickets/${id}/plan`),
  enhance: (id) => api.post(`${API_PREFIX}/tickets/${id}/enhance`),
  orchestrate: (id, auto) => api.post(`${API_PREFIX}/tickets/${id}/orchestrate`, { auto }),
  resetAgent: (id, agentId) => api.post(`${API_PREFIX}/tickets/${id}/reset-agent/${agentId}`),
  setPrUrl: (id, prUrl) => api.post(`${API_PREFIX}/tickets/${id}/set-pr-url`, { pr_url: prUrl }),
};

export const comments = {
  getForTicket: (ticketId) => api.get(`${API_PREFIX}/tickets/${ticketId}/comments`),
  create: (ticketId, content) => api.post(`${API_PREFIX}/tickets/${ticketId}/comments`, { content }),
  createSystem: (ticketId, content, authorName, apiKey) =>
    axios.post(`${API_BASE_URL}${API_PREFIX}/tickets/${ticketId}/comments/system`,
      { content, author_name: authorName },
      { headers: { 'X-API-Key': apiKey } }
    ),
};

export default api;
